import React, { useCallback, useEffect, useState } from 'react'

import IconComponent, {
  IconName,
  IconProps as SvgIconProps,
} from '@/components/atoms/Icon'

import styles from './Toast.module.scss'
import { ToastProps, ToastVariant } from './types/toast.types'

/**
 * Toast component for showing temporary notifications to the user
 */
export const Toast = ({
  children,
  variant = 'generic',
  colorScheme = 'black',
  position = 'top-right',
  Icon: IconProp,
  onClose,
  duration = 3000,
  className = '',
  showCloseButton = true,
  persistent = false,
  hideIcon = false,
  ...props
}: ToastProps) => {
  const [isVisible, setIsVisible] = useState(true)

  // Choose the appropriate ARIA role based on the variant
  const getAriaRole = (variant: ToastVariant) => {
    // For critical errors and warnings, use 'alert'
    if (variant === 'error' || variant === 'warning') {
      return 'alert' // Interrupt the user immediately
    }
    // For info and success, use 'status'
    return 'status' // Less invasive
  }

  // Choose the appropriate ARIA-LIVE priority
  const getAriaLive = (variant: ToastVariant) => {
    // For critical errors, use 'assertive'
    if (variant === 'error') {
      return 'assertive' // Interrupt the user immediately
    }
    // For info and success, use 'polite'
    return 'polite' // Wait for the screen reader to finish before announcing
  }

  // Handles the toast closure with animation using useCallback
  const handleClose = useCallback(() => {
    setIsVisible(false)

    // Delay to allow the exit animation
    setTimeout(() => {
      if (onClose) onClose()
    }, 300) // Duration of the animation
  }, [onClose])

  // Handles the toast closure after the specified duration
  useEffect(() => {
    // Does not automatically close if persistent or duration is 0
    if (persistent || !duration || duration <= 0) return

    const timer = setTimeout(() => {
      handleClose()
    }, duration)

    return () => clearTimeout(timer)
  }, [duration, handleClose, persistent])

  // Select the default icon based on the variant
  const DefaultIcon = getDefaultIcon(variant, IconProp)

  // Calculate CSS classes
  const toastClasses = [
    styles.toast,
    styles[`toast--${variant}`],
    isVisible ? styles['toast--visible'] : styles['toast--hidden'],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  // Calculate the color scheme based on the variant if not specified
  const toastColorScheme = colorScheme || getColorSchemeByVariant(variant)

  return (
    <div
      className={toastClasses}
      data-color-scheme={toastColorScheme}
      data-position={position}
      role={getAriaRole(variant)}
      aria-live={getAriaLive(variant)}
      aria-atomic='true'
      aria-relevant='additions'
      tabIndex={0}
      onKeyDown={(e) => {
        // Allows closing the toast with Escape or Delete
        if (e.key === 'Escape' || e.key === 'Delete') {
          handleClose()
        }
      }}
      {...props}
    >
      {!hideIcon && DefaultIcon && (
        <DefaultIcon
          iconName='InfoCircle'
          className={styles.toast__icon}
        />
      )}
      <div className={styles.toast__content}>{children}</div>
      {/* Always show the close button for persistent toasts, otherwise respect the showCloseButton prop */}
      {(persistent || showCloseButton) && (
        <button
          className={styles.toast__close}
          onClick={handleClose}
          aria-label='Chiudi notifica'
        >
          <IconComponent iconName='Close' />
        </button>
      )}
    </div>
  )
}

// Function to get the default icon based on the variant
const getDefaultIcon = (
  variant: ToastVariant,
  IconProp?: React.ComponentType<SvgIconProps> | string
): React.ComponentType<SvgIconProps> | undefined => {
  // If it's a React component, return it directly
  if (IconProp && typeof IconProp !== 'string') return IconProp

  // If it's a string, create a component that uses that string as the icon name
  if (typeof IconProp === 'string') {
    const DynamicIcon: React.FC<{ className?: string }> = ({ className }) => (
      <IconComponent
        iconName={IconProp as IconName}
        className={className}
      />
    )
    DynamicIcon.displayName = `Icon${IconProp}`
    return DynamicIcon
  }

  // Otherwise use the default icon based on the variant
  switch (variant) {
    case 'success':
      const SuccessIcon: React.FC<{ className?: string }> = ({ className }) => (
        <IconComponent
          iconName='CheckCircle'
          className={className}
        />
      )
      SuccessIcon.displayName = 'SuccessIcon'
      return SuccessIcon
    case 'error':
      const ErrorIcon: React.FC<{ className?: string }> = ({ className }) => (
        <IconComponent
          iconName='AlertTriangle'
          className={className}
        />
      )
      ErrorIcon.displayName = 'ErrorIcon'
      return ErrorIcon
    case 'warning':
      const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
        <IconComponent
          iconName='WarningCircle'
          className={className}
        />
      )
      WarningIcon.displayName = 'WarningIcon'
      return WarningIcon
    case 'info':
    case 'generic':
      const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
        <IconComponent
          iconName='InfoCircle'
          className={className}
        />
      )
      InfoIcon.displayName = 'InfoIcon'
      return InfoIcon
    default:
      return undefined
  }
}

// Function to get the color scheme based on the variant
const getColorSchemeByVariant = (variant: ToastVariant) => {
  switch (variant) {
    case 'generic':
      return 'black'
    case 'success':
      return 'green'
    case 'error':
      return 'red'
    case 'warning':
      return 'yellow'
    case 'info':
      return 'blue'
    default:
      return 'blue'
  }
}
